import { buildAuthHeaders } from "../lib/authFetch";
import {
  peekDesktopClientHeaders,
  primeDesktopClientHeaders,
} from "./desktopClientHeaders";

export const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:3000";
const CLIENT_APP = "desktop";
/**
 * Un error de request que LLEVA el status, además de tenerlo en el texto.
 *
 * El mensaje queda igual (`HTTP <status>: <body>`) a propósito: hay pantallas
 * que lo leen con `message.includes("401")`, y romperlas en el mismo cambio
 * sería cambiar dos cosas a la vez. Lo que agrega es un canal confiable — el
 * texto no distingue un 410 de un cuerpo que contenga "410", y el backend
 * ahora contesta 401, 410 y 429 con tres salidas distintas.
 */
export class ErrorDeRequest extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ErrorDeRequest";
  }
}

/**
 * El status de un error de request, o 0 si no lo trae.
 *
 * Se lee la PROPIEDAD y no `instanceof`: ese chequeo compara identidades de
 * clase y falla en silencio si el módulo llega duplicado —un bundle que lo
 * incluye dos veces, un test que lo mockea—. El resultado sería una rama que
 * nunca entra y nadie se entera.
 */
export function statusDeError(err: unknown): number {
  if (typeof err !== "object" || err === null) return 0;

  const status = (err as { status?: unknown }).status;

  return typeof status === "number" ? status : 0;
}

const RETRYABLE_HTTP_STATUS = new Set([502, 503, 504]);
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

const IS_ELECTRON =
  typeof window !== "undefined" && Boolean(window.electronAPI);

// Versión/plataforma del shell (X-Eriol-Version / X-Eriol-Platform): se pide
// por IPC al cargar el módulo y viaja en todas las requests del desktop; las
// autenticadas que salen antes de que conteste la esperan (ver
// getDesktopClientHeaders), las que no autentican no.
if (IS_ELECTRON) void primeDesktopClientHeaders();

function readCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)eriol_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function isRetryableMethod(method: string): boolean {
  return method.toUpperCase() === "GET";
}

function isRetryableNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (!(error instanceof Error)) return false;
  const normalizedMessage = error.message.toLowerCase();
  return (
    normalizedMessage.includes("failed to fetch") ||
    normalizedMessage.includes("fetch failed") ||
    normalizedMessage.includes("networkerror")
  );
}

function getRetryDelayMs(attempt: number): number {
  // attempt starts at 1
  return Math.min(300 * 2 ** (attempt - 1), 1500);
}

async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

async function buildHeaders(
  method: string,
  auth: boolean,
): Promise<Record<string, string>> {
  // Authenticated requests: the shared builder (Authorization in Electron,
  // cookie + CSRF in the browser, desktop version headers) — the same one
  // authFetch and the donation services use, so they cannot drift apart.
  if (auth) return buildAuthHeaders(method);

  // Sin sesión, la versión viaja solo si ya se conoce: el backend la anota
  // únicamente en las requests que autentican, así que esperar al IPC acá
  // (login, catálogos públicos del arranque) era demora sin dato a cambio.
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Client-App": CLIENT_APP,
    ...peekDesktopClientHeaders(),
  };

  if (!SAFE_METHODS.has(method.toUpperCase())) {
    // Unauthenticated mutations still need the CSRF token if a session
    // cookie happens to be present — backend middleware enforces it.
    const csrf = readCsrfToken();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }

  return headers;
}

async function request<T>(
  method: string,
  endpoint: string,
  data?: unknown,
  auth = false,
): Promise<T> {
  const normalizedMethod = method.toUpperCase();

  // credentials:include ships the HttpOnly eriol_session cookie on cross-origin
  // calls to api.eriol.io. Backend CORS already has Allow-Credentials: true.
  // Harmless in Electron context.
  const opts: RequestInit = {
    method: normalizedMethod,
    headers: await buildHeaders(normalizedMethod, auth),
    credentials: "include",
  };
  if (data) opts.body = JSON.stringify(data);

  const maxAttempts = isRetryableMethod(normalizedMethod) ? 4 : 1;
  const requestUrl = `${API_BASE_URL}${endpoint}`;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let response: Response;

    try {
      response = await fetch(requestUrl, opts);
    } catch (error) {
      const shouldRetry =
        attempt < maxAttempts && isRetryableNetworkError(error);
      if (!shouldRetry) throw error;
      await sleep(getRetryDelayMs(attempt));
      continue;
    }

    if (!response.ok) {
      if (response.status === 401 && auth) {
        window.dispatchEvent(new CustomEvent("auth:session-expired"));
      }
      const text = await response.text();
      const shouldRetry =
        attempt < maxAttempts && RETRYABLE_HTTP_STATUS.has(response.status);
      if (shouldRetry) {
        await sleep(getRetryDelayMs(attempt));
        continue;
      }
      throw new ErrorDeRequest(`HTTP ${response.status}: ${text}`, response.status);
    }

    return response.json();
  }

  throw new Error(
    `Request failed after ${maxAttempts} attempts: ${normalizedMethod} ${endpoint}`,
  );
}

export const api = {
  get: <T>(endpoint: string) => request<T>("GET", endpoint),
  post: <T>(endpoint: string, data: unknown) =>
    request<T>("POST", endpoint, data),
  put: <T>(endpoint: string, data: unknown) =>
    request<T>("PUT", endpoint, data),

  authGet: <T>(endpoint: string) =>
    request<T>("GET", endpoint, undefined, true),
  authPost: <T>(endpoint: string, data: unknown) =>
    request<T>("POST", endpoint, data, true),
  authPut: <T>(endpoint: string, data: unknown) =>
    request<T>("PUT", endpoint, data, true),
  authPatch: <T>(endpoint: string, data: unknown) =>
    request<T>("PATCH", endpoint, data, true),
  authDelete: <T>(endpoint: string) =>
    request<T>("DELETE", endpoint, undefined, true),

  authUpload: async <T>(endpoint: string, formData: FormData): Promise<T> => {
    // No Content-Type: el browser pone el boundary multipart.
    const headers = await buildAuthHeaders("POST", { json: false });
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "POST",
      headers,
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      if (res.status === 401) {
        window.dispatchEvent(new CustomEvent("auth:session-expired"));
      }
      throw new ErrorDeRequest(
        `HTTP ${res.status}: ${await res.text()}`,
        res.status,
      );
    }
    return res.json();
  },
};
